import { useState } from "react";
import Modal from "@/components/base/Modal";
import { useToast } from "@/components/feature/ToastProvider";
import type { RpsHand } from "@/lib/types";
import { writeRps } from "../api";
import { HANDS } from "../utils";

interface Props {
  open: boolean;
  onClose: () => void;
  /** 성공 시 생성된 판 번호 전달 */
  onCreated: (no?: number) => void;
}

const MIN_PRESETS = [
  { label: "30분", value: 30 },
  { label: "50분", value: 50 },
  { label: "1시간", value: 60 },
  { label: "2시간", value: 120 },
];

// «판 열기» 등록 모달.
// 등록자는 자기 손(가위/바위/보)을 미리 고른다. 마감 전까지 아무에게도 보이지 않는다.
export default function RpsWriteModal({ open, onClose, onCreated }: Props) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [hand, setHand] = useState<RpsHand | "">("");
  const [limitCnt, setLimitCnt] = useState(5);
  const [limitMin, setLimitMin] = useState(50);
  const [customMin, setCustomMin] = useState(false);
  const [reward, setReward] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputClass =
    "w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

  const reset = () => {
    setSubject("");
    setHand("");
    setLimitCnt(5);
    setLimitMin(50);
    setCustomMin(false);
    setReward("");
  };

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    const s = subject.trim();
    if (!s) {
      toast("제목을 입력해 주세요.", "info");
      return;
    }
    if (!hand) {
      toast("내 손을 골라 주세요.", "info");
      return;
    }
    const min = Number(limitMin);
    if (!Number.isFinite(min) || min < 5 || min > 1440) {
      toast("타임리미트는 5분 ~ 1440분(24시간) 사이로 정해 주세요.", "info");
      return;
    }
    setSubmitting(true);
    try {
      const res = await writeRps({
        subject: s,
        hand,
        limit_cnt: Math.max(0, Number(limitCnt) || 0),
        limit_min: min,
        reward: reward.trim(),
      });
      toast(res.msg || "판을 열었습니다!", "success");
      reset();
      onCreated(res.data?.no);
    } catch (e) {
      toast(e instanceof Error ? e.message : "등록에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} maxWidth="max-w-md" labelledBy="rps-write-title">
      <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
        <h2
          id="rps-write-title"
          className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950"
        >
          <span aria-hidden="true">✊</span>
          판 열기
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="닫기"
          className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
        >
          <i className="ri-close-line text-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div className="space-y-4 px-5 py-5">
        <div>
          <label
            htmlFor="rps-subject"
            className="mb-1.5 block text-sm font-semibold text-foreground-700"
          >
            제목 <span className="text-accent-600">*</span>
          </label>
          <input
            id="rps-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={80}
            placeholder="예) 점심내기 가위바위보"
            className={inputClass}
          />
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">
            내 손 <span className="text-accent-600">*</span>
          </p>
          <div className="grid grid-cols-3 gap-2">
            {HANDS.map((h) => {
              const selected = hand === h.code;
              return (
                <button
                  key={h.code}
                  type="button"
                  onClick={() => setHand(h.code)}
                  aria-pressed={selected}
                  className={`flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-lg border text-sm font-bold transition-colors ${
                    selected
                      ? "border-primary-400 bg-primary-50 text-foreground-950"
                      : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-200 hover:bg-background-100"
                  }`}
                >
                  <span className="text-3xl leading-none" aria-hidden="true">
                    {h.emoji}
                  </span>
                  {h.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-foreground-400">
            선택한 손은 마감될 때까지 아무에게도 보이지 않습니다.
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <label
              htmlFor="rps-count"
              className="mb-1.5 block text-sm font-semibold text-foreground-700"
            >
              선착순 인원
            </label>
            <input
              id="rps-count"
              type="number"
              min={0}
              max={99}
              value={limitCnt}
              onChange={(e) => setLimitCnt(Number(e.target.value))}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-foreground-400">0 이면 인원 제한 없음</p>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">타임리미트</p>
          <div className="flex flex-wrap gap-2">
            {MIN_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => {
                  setLimitMin(preset.value);
                  setCustomMin(false);
                }}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  !customMin && limitMin === preset.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCustomMin(true)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                customMin
                  ? "border-primary-400 bg-primary-50 text-primary-700"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
              }`}
            >
              직접입력
            </button>
          </div>
          {customMin && (
            <div className="mt-2.5 flex items-center gap-2">
              <input
                type="number"
                min={5}
                max={1440}
                value={limitMin}
                onChange={(e) => setLimitMin(Number(e.target.value))}
                className={`${inputClass} w-28`}
                aria-label="타임리미트(분)"
              />
              <span className="text-sm text-foreground-500">분 (5 ~ 1440)</span>
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="rps-reward"
            className="mb-1.5 block text-sm font-semibold text-foreground-700"
          >
            이긴 사람 보상 (선택)
          </label>
          <input
            id="rps-reward"
            type="text"
            value={reward}
            onChange={(e) => setReward(e.target.value)}
            maxLength={60}
            placeholder="비워두면 그냥 재미로 하는 게임입니다"
            className={inputClass}
          />
        </div>

        <p className="rounded-lg bg-background-100 px-3 py-2 text-xs text-foreground-500">
          정원이 차거나 시간이 지나면 자동으로 마감되고, 그때 모든 손이 공개됩니다.
        </p>
      </div>

      <div className="flex gap-2 border-t border-background-200 px-5 py-4">
        <button
          type="button"
          onClick={close}
          disabled={submitting}
          className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
        >
          취소
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          {submitting ? "여는 중…" : "판 열기"}
        </button>
      </div>
    </Modal>
  );
}